// src/pages/SetupWizard.jsx
// Setup Wizard: Profile → Admin → Behavior → Phone → Done
// - Preloads Company Profile
// - Saves Profile & Behavior to /company-profile (upsert)
// - Auto-creates tenant on Step 1
// - AUTOCOMPLETE: Business type (industry) + Timezone via /company-profile/* endpoints

import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../lib/api";

const STEPS = ["Profile", "Admin", "Behavior", "Phone", "Done"];

const slugify = (s = "") =>
  s.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

const DEFAULT_BEHAVIOR_PROMPT = `
Only suggest pressing * to speak to a real person if you don't have information for the customer.
When asked to speak to a real person by pressing *, respond that an attendant has been notified and will respond shortly.

CORE BEHAVIOR
1) Understand the question.
2) Use the business data only when relevant.
3) Use the internal knowledge base (Informative Panel and Predefined Messages) when available.
4) Make sure replies are informative but still sound natural, like a real person.
5) Respond clearly and politely, like a human assistant would.
6) If the message is generic (e.g., “hi”, “hello”, “good morning”, “oi tudo bem?”), reply simply and naturally and ask how you can help. Do NOT return HUMAN_HANDOFF for these.
7) Language-availability questions (e.g., “do you speak portuguese/spanish/english?”): confirm you can chat in that language and continue. Do NOT return HUMAN_HANDOFF for these.
8) Use business information only if the question relates to it.
9) Do not mention the business name or service unless it’s clearly relevant.
10) If the customer asks about address, website, or hours, use the corresponding values from the Company Profile.
11) Use the Company Profile “Business Type” as guidance for tone and behavior.
12) Never state what language was detected — just answer in it.
13) Do NOT provide the company phone number.

DATA POLICY
- Answer ONLY with the information provided by the application: Company Profile, Internal Information, and Predefined Messages.
- Do NOT invent facts beyond these sources.
- If the request requires information not covered by these sources (excluding basic greetings or language-availability questions), return exactly:
  HUMAN_HANDOFF

PREDEFINED MESSAGES
- If you reuse any Predefined Message content, at the very end of your reply append a new line:
  USED_IDS: <comma-separated ids>
- Do not expose the IDs to the customer; the system will remove this line before sending.

LANGUAGE
- ALWAYS reply strictly in the customer’s language from the last message. Do NOT be bilingual.
- Keep replies short and helpful (1–4 sentences unless more is clearly necessary).

STYLE
- Professional, warm, concise. Avoid legal advice or guarantees unless explicitly present in the provided data.

AVAILABILITY
- When the customer asks about availability for a specific reference:
  - Use the Availability API when configured.
  - Hospitality: even if available, share the website/listing link for booking.
  - Services/Medical/Handyman: if available, offer the next steps; only create a booking if the application confirms it.
  - If the API is not configured or returns an error: share the Company Profile.website link for real-time availability.
- Never promise confirmed booking; just inform availability and next steps.
`.trim();

export default function SetupWizard() {
  const [step, setStep] = useState(0);
  const stepName = useMemo(() => STEPS[step] || "Setup", [step]);

  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Profile state
  const [profile, setProfile] = useState({
    companyName: "",
    website: "",
    industryType: "",
    timezone: "",
  });

  // Autocomplete options
  const [industryOptions, setIndustryOptions] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);

  // Admin (optional)
  const [admin, setAdmin] = useState({ name: "", email: "", password: "" });

  // Behavior
  const [behavior, setBehavior] = useState({
    behaviorPrompt: DEFAULT_BEHAVIOR_PROMPT,
  });

  // Tenant (set on Step 1)
  const [tenant, setTenant] = useState({ id: "", slug: "" });

  // Phone
  const [country, setCountry] = useState("US");
  const [areaCode, setAreaCode] = useState("");
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState("");
  const [subaccountSid, setSubaccountSid] = useState("");
  const [busyPhone, setBusyPhone] = useState(false);

  const next = () => {
    setMessage("");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => {
    setMessage("");
    setStep((s) => Math.max(s - 1, 0));
  };

  // --------- preload helpers ----------
  async function fetchCompanyProfile(updateMsg = false) {
    try {
      setLoadingProfile(true);
      const data = await apiGet("/company-profile");
      if (data && typeof data === "object") {
        const name = data.name || data.orgName || "";
        const site = data.website || "";
        const industry = data.industryType || data.industry || "";
        const tz = data.timezone || "";

        setProfile({
          companyName: name,
          website: site,
          industryType: industry,
          timezone: tz,
        });

        const bp =
          data?.ai?.behaviorPrompt ||
          data?.behaviorPrompt ||
          DEFAULT_BEHAVIOR_PROMPT;
        setBehavior({ behaviorPrompt: bp });

        const slug =
          data?.tenantSlug || (name ? slugify(name) : "");
        if (data?.tenantId) setTenant({ id: data.tenantId, slug });
      }
      if (updateMsg) setMessage("Loaded Company Profile.");
    } catch (e) {
      if (updateMsg) setMessage(e?.response?.data?.error || "Company Profile not found yet.");
    } finally {
      setLoadingProfile(false);
    }
  }

  async function fetchAutocomplete() {
    try {
      const [inds, tzs] = await Promise.all([
        apiGet("/company-profile/industries").catch(() => []),
        apiGet("/company-profile/timezones").catch(() => []),
      ]);

      const fallbackIndustries = ["Law", "Cleaning", "Vacation Rental", "Dental", "Real Estate", "Hospitality", "Medical", "IT Services"];
      const fallbackTimezones = ["America/Los_Angeles", "America/New_York", "America/Sao_Paulo", "Europe/London", "Europe/Lisbon"];

      setIndustryOptions(Array.isArray(inds) && inds.length ? inds : fallbackIndustries);
      setTimezoneOptions(Array.isArray(tzs) && tzs.length ? tzs : fallbackTimezones);
    } catch {
      // quietly keep defaults
    }
  }

  // mount
  useEffect(() => {
    fetchCompanyProfile(false);
    fetchAutocomplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refresh when visiting Step 1 again
  useEffect(() => {
    if (step === 0) fetchCompanyProfile(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // --------- Step 1: save profile + auto tenant ----------
  async function saveProfile() {
    setSaving(true);
    setMessage("");
    try {
      const payload = {
        name: profile.companyName?.trim(),
        website: profile.website?.trim(),
        industryType: (profile.industryType || "").trim(),
        timezone: (profile.timezone || "").trim(),
        orgName: profile.companyName?.trim(),
        tenantSlug: slugify(profile.companyName || ""),
      };
      if (!payload.name) throw new Error("Company name is required.");

      const saved =
        (await apiPost("/company-profile?upsert=1&_method=PUT", payload)) || {};

      setProfile({
        companyName: saved.name || payload.name,
        website: saved.website ?? payload.website ?? "",
        industryType: saved.industryType ?? payload.industryType ?? "",
        timezone: saved.timezone ?? payload.timezone ?? "",
      });

      await fetchCompanyProfile(false);

      // tenant
      try {
        const name = payload.name;
        const slug = slugify(name);
        const t = await apiPost("/api/tenants", { name, slug });
        const id = t?.tenant?._id || t?.id || "";
        if (id) {
          setTenant({ id, slug });
          setMessage(`Profile saved. Tenant ready: ${slug}`);
        } else {
          setMessage("Profile saved. Tenant created (no id returned).");
        }
      } catch (e2) {
        const msg =
          e2?.response?.data?.error ||
          "Profile saved. Tenant API unavailable; you can still continue.";
        setMessage(msg);
      }

      next();
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || "Error saving profile";
      setMessage(msg);
    } finally {
      setSaving(false);
    }
  }

  // --------- Step 2: admin (optional) ----------
  async function saveAdmin() {
    setSaving(true);
    setMessage("");
    try {
      if (!admin.name || !admin.email || !admin.password) {
        throw new Error("Name, email and password are required.");
      }
      setMessage("Admin captured (local). You can manage users later.");
      next();
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || "Error on admin step";
      setMessage(msg);
    } finally {
      setSaving(false);
    }
  }

  // --------- Step 3: behavior ----------
  async function saveBehavior() {
    setSaving(true);
    setMessage("");
    try {
      const payload = {
        ai: {
          behaviorPrompt:
            behavior.behaviorPrompt?.trim() || DEFAULT_BEHAVIOR_PROMPT,
          replyInEnglishAndPortuguese: false,
          useInformativePanel: true,
          usePredefinedMessages: true,
        },
      };
      const saved = await apiPost("/company-profile", payload);
      const bp =
        saved?.ai?.behaviorPrompt ||
        saved?.behaviorPrompt ||
        payload.ai.behaviorPrompt;
      setBehavior({ behaviorPrompt: bp });
      await fetchCompanyProfile(false);
      setMessage("Behavior saved.");
      next();
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || "Error saving behavior";
      setMessage(msg);
    } finally {
      setSaving(false);
    }
  }

  // --------- Step 4: phone ----------
  async function createSubaccount() {
    if (!tenant.id) return setMessage("Tenant missing. Go back to Profile and save again.");
    setBusyPhone(true);
    setMessage("");
    try {
      const data = await apiPost(`/api/tenants/${tenant.id}/twilio/subaccount`, {});
      if (data?.subaccountSid) setSubaccountSid(data.subaccountSid);
      setMessage(data?.message || "Subaccount created.");
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || "Failed to create subaccount (you can skip).";
      setMessage(msg);
    } finally {
      setBusyPhone(false);
    }
  }

  async function listNumbers() {
    if (!tenant.id) return setMessage("Tenant missing. Go back to Profile and save again.");
    if (!country) return setMessage("Country is required (e.g., US).");
    setBusyPhone(true);
    setMessage("");
    try {
      const qs = new URLSearchParams({
        country,
        ...(areaCode ? { areaCode } : {}),
      }).toString();
      const data = await apiGet(`/api/tenants/${tenant.id}/twilio/available-numbers?${qs}`);
      const arr = Array.isArray(data) ? data : data?.numbers || [];
      setAvailableNumbers(arr);
      setMessage(`Found ${arr.length} numbers${areaCode ? ` (area ${areaCode})` : ""}.`);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || "Failed to list numbers (you can skip).";
      setMessage(msg);
    } finally {
      setBusyPhone(false);
    }
  }

  async function purchaseNumber() {
    if (!tenant.id) return setMessage("Tenant missing. Go back to Profile and save again.");
    if (!selectedNumber) return setMessage("Select a number first.");
    setBusyPhone(true);
    setMessage("");
    try {
      const data = await apiPost(`/api/tenants/${tenant.id}/twilio/purchase-number`, {
        phoneNumber: selectedNumber,
      });
      setMessage(data?.message || `Number ${selectedNumber} purchased. All set!`);
      setStep(STEPS.length - 1);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || "Failed to purchase number (you can skip).";
      setMessage(msg);
    } finally {
      setBusyPhone(false);
    }
  }

  // --------- UI ----------
  function renderProfile() {
    return (
      <div className="space-y-4">
        {loadingProfile ? (
          <div className="text-sm text-gray-500">Loading profile…</div>
        ) : null}

        <div>
          <label className="block text-sm mb-1">Company name</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Acme Law"
            value={profile.companyName}
            onChange={(e) => setProfile((s) => ({ ...s, companyName: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm mb-1">Website</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder="https://example.com"
            value={profile.website}
            onChange={(e) => setProfile((s) => ({ ...s, website: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm mb-1">Business type (industry)</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            list="industryList"
            placeholder="Law"
            value={profile.industryType}
            onChange={(e) => setProfile((s) => ({ ...s, industryType: e.target.value }))}
          />
          <datalist id="industryList">
            {industryOptions.map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
          <p className="text-xs text-gray-500 mt-1">Free text with suggestions from other customers.</p>
        </div>

        <div>
          <label className="block text-sm mb-1">Timezone (optional)</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            list="timezoneList"
            placeholder="America/Sao_Paulo"
            value={profile.timezone}
            onChange={(e) => setProfile((s) => ({ ...s, timezone: e.target.value }))}
          />
          <datalist id="timezoneList">
            {timezoneOptions.map((tz) => (
              <option key={tz} value={tz} />
            ))}
          </datalist>
          <p className="text-xs text-gray-500 mt-1">IANA format, e.g., America/Sao_Paulo.</p>
        </div>

        <button
          onClick={saveProfile}
          disabled={saving}
          className="rounded-xl px-4 py-2 bg-black text-white"
        >
          {saving ? "Saving..." : "Save & continue"}
        </button>
      </div>
    );
  }

  function renderAdmin() {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm mb-1">Admin full name</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder="John Doe"
            value={admin.name}
            onChange={(e) => setAdmin((s) => ({ ...s, name: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Admin email</label>
          <input
            type="email"
            className="w-full border rounded-lg px-3 py-2"
            placeholder="you@example.com"
            value={admin.email}
            onChange={(e) => setAdmin((s) => ({ ...s, email: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Admin password</label>
          <input
            type="password"
            className="w-full border rounded-lg px-3 py-2"
            placeholder="••••••••"
            value={admin.password}
            onChange={(e) => setAdmin((s) => ({ ...s, password: e.target.value }))}
          />
        </div>

        <div className="flex gap-2">
          <button onClick={back} className="rounded-xl px-4 py-2 border">Back</button>
          <button
            onClick={saveAdmin}
            disabled={saving}
            className="rounded-xl px-4 py-2 bg-black text-white"
          >
            {saving ? "Saving..." : "Save & continue"}
          </button>
        </div>
      </div>
    );
  }

  function renderBehavior() {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm mb-1">Behavior prompt (default provided)</label>
          <textarea
            className="w-full border rounded-lg px-3 py-2 min-h-[220px]"
            value={behavior.behaviorPrompt}
            onChange={(e) => setBehavior({ behaviorPrompt: e.target.value })}
          />
          <p className="text-xs text-gray-500 mt-1">
            You can edit this anytime in Setup → Profile.
          </p>
        </div>

        <div className="flex gap-2">
          <button onClick={back} className="rounded-xl px-4 py-2 border">Back</button>
          <button
            onClick={saveBehavior}
            disabled={saving}
            className="rounded-xl px-4 py-2 bg-black text-white"
          >
            {saving ? "Saving..." : "Save & continue"}
          </button>
        </div>
      </div>
    );
  }

  function renderPhone() {
    const disabled = !tenant.id;

    return (
      <div className="space-y-5">
        {message ? (
          <div className="text-sm bg-gray-50 border rounded p-3 whitespace-pre-wrap">{message}</div>
        ) : null}

        {!tenant.id ? (
          <div className="rounded-xl border p-4 bg-amber-50">
            <h3 className="text-sm font-medium">Tenant not ready</h3>
            <p className="text-sm text-amber-700">
              A tenant is created automatically when you save the Profile step. Go back to Profile,
              fill the company name and save again. You may still skip this step for now.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border p-4">
            <div className="text-sm text-gray-600">
              Tenant: <span className="font-mono">{tenant.slug}</span> (id:{" "}
              <span className="font-mono">{tenant.id}</span>)
            </div>
          </div>
        )}

        <div className={`rounded-xl border p-4 ${disabled ? "opacity-60 pointer-events-none" : ""}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Twilio subaccount</h3>
              <p className="text-sm text-gray-500">
                Create a Twilio subaccount under your main account.
              </p>
            </div>
            <button
              onClick={createSubaccount}
              disabled={busyPhone || disabled}
              className="rounded-lg px-3 py-2 bg-black text-white disabled:opacity-50"
            >
              {busyPhone ? "Working..." : "Create subaccount"}
            </button>
          </div>
          {subaccountSid ? (
            <div className="mt-2 text-xs text-gray-600">Subaccount SID: {subaccountSid}</div>
          ) : null}
        </div>

        <div className={`rounded-xl border p-4 space-y-3 ${disabled ? "opacity-60 pointer-events-none" : ""}`}>
          <h3 className="text-lg font-semibold">Choose a number</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm mb-1">Country</label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="US"
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Area code (optional)</label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                value={areaCode}
                onChange={(e) => setAreaCode(e.target.value)}
                placeholder="310"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={listNumbers}
                disabled={busyPhone || disabled}
                className="w-full rounded-lg px-3 py-2 bg-black text-white disabled:opacity-50"
              >
                {busyPhone ? "Searching..." : "List available"}
              </button>
            </div>
          </div>

          {availableNumbers?.length ? (
            <div className="mt-3">
              <label className="block text-sm mb-1">Select a number</label>
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={selectedNumber}
                onChange={(e) => setSelectedNumber(e.target.value)}
              >
                <option value="">-- choose a number --</option>
                {availableNumbers.map((n) => {
                  const val = typeof n === "string" ? n : n.phoneNumber || n.friendlyName;
                  return (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  );
                })}
              </select>

              <button
                onClick={purchaseNumber}
                disabled={busyPhone || !selectedNumber || disabled}
                className="mt-3 rounded-lg px-3 py-2 bg-black text-white disabled:opacity-50"
              >
                {busyPhone ? "Purchasing..." : "Purchase number"}
              </button>
            </div>
          ) : null}
        </div>

        <div className="flex gap-2">
          <button onClick={back} className="rounded-xl px-4 py-2 border">Back</button>
          <button onClick={() => setStep(STEPS.length - 1)} className="rounded-xl px-4 py-2 bg-black text-white">
            Skip to done
          </button>
        </div>
      </div>
    );
  }

  function renderDone() {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">All set!</h3>
        <p className="text-sm text-gray-600">
          Your setup is complete. You can now use the assistant and manage your numbers later.
        </p>
        <div className="flex gap-2">
          <a href="/dashboard" className="rounded-xl px-4 py-2 bg-black text-white inline-flex">
            Open dashboard
          </a>
          <a href="/history" className="rounded-xl px-4 py-2 border inline-flex">
            Go to inbox
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-start justify-center bg-gray-50 p-6">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Setup Wizard</h1>
            <p className="text-sm text-gray-500">
              Step {step + 1} of {STEPS.length}: {stepName}
            </p>
          </div>
        </div>

        {message ? (
          <div className="mt-4 text-sm bg-gray-50 border border-gray-200 rounded-md p-3 whitespace-pre-wrap">
            {message}
          </div>
        ) : null}

        <div className="mt-6">
          {step === 0 && renderProfile()}
          {step === 1 && renderAdmin()}
          {step === 2 && renderBehavior()}
          {step === 3 && renderPhone()}
          {step === 4 && renderDone()}
        </div>
      </div>
    </div>
  );
}
